// Unified image types for the entire application

export interface OptimizedImageObject {
  id: string;
  original: string;
  mobile: string;
  tablet: string;
  desktop: string;
  large: string;
  mobileWebp: string;
  tabletWebp: string;
  desktopWebp: string;
  largeWebp: string;
}

// Union type for all possible image sources
export type ImageSource =
  | OptimizedImageObject
  | File
  | string
  | undefined
  | null;

// Interface for image processing utilities
export interface ImageUtils {
  getBestSource: (image: ImageSource, size?: ImageSize) => string;
  createBlobUrl: (file: File) => string;
  cleanupBlobUrl: (url: string) => void;
  isOptimizedImage: (image: any) => image is OptimizedImageObject;
  isFileObject: (image: any) => image is File;
}

// Image size hints for responsive images
export type ImageSize = "small" | "medium" | "large" | "xlarge";

// Breakpoints for responsive images
export const IMAGE_BREAKPOINTS = {
  small: 320, // mobile
  medium: 640, // tablet
  large: 1024, // desktop
  xlarge: 1920, // large desktop
} as const;

// Default sizes string for responsive images
export const DEFAULT_SIZES =
  "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw";

// Cache for blob URLs to prevent memory leaks
export class BlobUrlCache {
  private cache = new Map<string, string>();
  private refCounts = new Map<string, number>();

  create(file: File, key?: string): string {
    const cacheKey = key || `${file.name}_${file.size}_${file.lastModified}`;

    if (this.cache.has(cacheKey)) {
      this.refCounts.set(cacheKey, (this.refCounts.get(cacheKey) || 0) + 1);
      return this.cache.get(cacheKey)!;
    }

    const url = URL.createObjectURL(file);
    this.cache.set(cacheKey, url);
    this.refCounts.set(cacheKey, 1);
    return url;
  }

  release(url: string): void {
    for (const [key, cachedUrl] of this.cache.entries()) {
      if (cachedUrl === url) {
        const refCount = this.refCounts.get(key) || 0;
        if (refCount <= 1) {
          URL.revokeObjectURL(url);
          this.cache.delete(key);
          this.refCounts.delete(key);
        } else {
          this.refCounts.set(key, refCount - 1);
        }
        break;
      }
    }
  }

  clear(): void {
    for (const url of this.cache.values()) {
      URL.revokeObjectURL(url);
    }
    this.cache.clear();
    this.refCounts.clear();
  }
}

// Global blob URL cache instance
export const globalBlobCache = new BlobUrlCache();

// Utility functions for image processing
export const imageUtils: ImageUtils = {
  getBestSource: (image: ImageSource, size: ImageSize = "medium"): string => {
    if (!image) {
      return "";
    }

    if (typeof image === "string") {
      return image;
    }

    if (imageUtils.isFileObject(image)) {
      return globalBlobCache.create(image);
    }

    if (imageUtils.isOptimizedImage(image)) {
      // Choose the best resolution based on size
      switch (size) {
        case "small":
          return (
            image.mobileWebp || image.mobile || image.tablet || image.original
          );
        case "medium":
          return (
            image.tabletWebp || image.tablet || image.desktop || image.original
          );
        case "large":
          return (
            image.desktopWebp || image.desktop || image.large || image.original
          );
        case "xlarge":
          return (
            image.largeWebp || image.large || image.desktop || image.original
          );
        default:
          return image.tabletWebp || image.tablet || image.original;
      }
    }

    return "";
  },

  createBlobUrl: (file: File): string => {
    return globalBlobCache.create(file);
  },

  cleanupBlobUrl: (url: string): void => {
    globalBlobCache.release(url);
  },

  isOptimizedImage: (image: any): image is OptimizedImageObject => {
    return (
      image &&
      typeof image === "object" &&
      "mobile" in image &&
      "tablet" in image &&
      "desktop" in image &&
      typeof image.mobile === "string"
    );
  },

  isFileObject: (image: any): image is File => {
    return typeof File !== "undefined" && image instanceof File;
  },
};

// Helper to get responsive image props for Next.js Image component
export interface ResponsiveImageProps {
  src: string;
  srcSet?: string;
  sizes?: string;
}

export const getResponsiveImageProps = (
  image: ImageSource,
  sizes: string = DEFAULT_SIZES,
): ResponsiveImageProps => {
  if (!image) {
    return { src: "" };
  }

  if (typeof image === "string" || imageUtils.isFileObject(image)) {
    return {
      src: imageUtils.getBestSource(image),
      sizes,
    };
  }

  if (imageUtils.isOptimizedImage(image)) {
    // Create srcSet for responsive images
    const srcSet = [
      `${image.mobileWebp || image.mobile} 320w`,
      `${image.tabletWebp || image.tablet} 640w`,
      `${image.desktopWebp || image.desktop} 1024w`,
      `${image.largeWebp || image.large} 1920w`,
    ]
      .filter((src) => !src.startsWith("undefined"))
      .join(", ");

    return {
      src: image.tablet || image.desktop || image.original,
      srcSet: srcSet || undefined,
      sizes,
    };
  }

  return { src: "" };
};
